import { Alert, Badge, Button, Card, CardBody, CardFooter, CardHeader, CurrencyDisplay, Icon, Input, InputWithTags, Modal, RadioGroup, Select, SelectOptionType, Tabs } from '@aidkitorg/component-library';
import React, { useCallback, useEffect, useState } from 'react';
import { useAccounts } from '../../../Contexts/AccountsContext';
import { accountKindToName } from '../AccountsPage';
import { useLocalizedStrings } from '../../../Localization';
import { Account } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import { AccountBalanceReminder, AccountBalanceReminderConfig, EditPaymentReminderParams, PaymentReminder } from '@aidkitorg/aidkit/lib/admin_panel/reminders';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { generateDateString, onlyEmails } from '../../../utils/adminPanel.utils';

type CreateReminderTabProps = {
  setRemindersModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  availableReminderTypes?: string[]
}

const CreateReminderTab: React.FC<CreateReminderTabProps> = ({ setRemindersModalOpen, availableReminderTypes }) => {
  const L = useLocalizedStrings()

  const [reminderType, setReminderType] = useState<string>()
  const [account, setAccount] = useState<Account['kind']>('unknown')
  const [thresholdAmount, setThresholdAmount] = useState<string>()
  const [recipients, setRecipients] = useState<Array<string>>([])
  
  // Payment failure reminder state
  const [failureFrequency, setFailureFrequency] = useState<'immediate' | 'hourly' | 'daily'>('immediate')
  const [failureRecipients, setFailureRecipients] = useState<Array<string>>([])
  
  // Payment status summary reminder state
  const [summaryFrequency, setSummaryFrequency] = useState<'daily' | 'weekly'>('daily')
  const [summaryRecipients, setSummaryRecipients] = useState<Array<string>>([])

  const [reminderCreated, setReminderCreated] = useState(false)

  const { 
    accounts, 
    reminderCallInFlight,
    createAccountBalanceReminder,
    createPaymentFailureReminder,
    createPaymentStatusSummaryReminder,
    fetchPaymentReminders
  } = useAccounts()

  const [createDisabled, setCreateDisabled] = useState(true)

  const ACCOUNTS_VALUES: Array<SelectOptionType> = []
  accounts.forEach((account) => {
    const option = { value: account.kind, label: accountKindToName(account.kind) }
    ACCOUNTS_VALUES.push(option)
  })

  useEffect(() => {
    let disableSubmit = !reminderType || reminderCallInFlight

    if (reminderType === 'account_balance') {
      disableSubmit = disableSubmit ||
        account == 'unknown' ||
        isNaN(Number(thresholdAmount)) || 
        Number(thresholdAmount) < 0 || 
        recipients.length === 0 ||
        recipients.join(',') == ''
    } else if (reminderType === 'payment_failure') {
      disableSubmit = disableSubmit ||
        failureRecipients.length === 0 ||
        failureRecipients.join(',') == ''
    } else if (reminderType === 'payment_status_summary') {
      disableSubmit = disableSubmit ||
        summaryRecipients.length === 0 ||
        summaryRecipients.join(',') == ''
    }

    setCreateDisabled(disableSubmit)
  }, [reminderType, account, thresholdAmount, recipients, failureRecipients, summaryRecipients, reminderCallInFlight])

  const createReminder = useCallback(async () => {
    let result = false

    if (reminderType === 'account_balance') {
      const reminderConfig: AccountBalanceReminderConfig = {
        account: account,
        thresholdInCents: Number(thresholdAmount) * 100,
        emails: recipients,
      }
      result = await createAccountBalanceReminder(reminderConfig)
      if (result) {
        setThresholdAmount("")
        setRecipients([])
        setAccount('unknown')
      }
    } else if (reminderType === 'payment_failure') {
      result = await createPaymentFailureReminder({
        frequency: failureFrequency,
        emails: failureRecipients,
      })
      if (result) {
        setFailureRecipients([])
        setFailureFrequency('immediate')
      }
    } else if (reminderType === 'payment_status_summary') {
      result = await createPaymentStatusSummaryReminder({
        frequency: summaryFrequency,
        emails: summaryRecipients,
      })
      if (result) {
        setSummaryRecipients([])
        setSummaryFrequency('daily')
      }
    }

    if (result) {
      setReminderCreated(true)
      // Refresh payment reminders if we created a payment reminder
      if (reminderType === 'payment_failure' || reminderType === 'payment_status_summary') {
        await fetchPaymentReminders()
      }
    }
  }, [
    reminderType,
    account,
    thresholdAmount,
    recipients,
    failureFrequency,
    failureRecipients,
    summaryFrequency,
    summaryRecipients,
    createAccountBalanceReminder,
    createPaymentFailureReminder,
    createPaymentStatusSummaryReminder,
    fetchPaymentReminders,
    setRemindersModalOpen
  ])

  const allReminderTypeOptions = [
    { label: L.admin_panel.reminders.account_balance, value: 'account_balance' },
    { label: L.admin_panel.reminders.payment_failure, value: 'payment_failure' },
    { label: L.admin_panel.reminders.payment_status_summary, value: 'payment_status_summary' },
  ]

  const reminderTypeOptions = availableReminderTypes
    ? allReminderTypeOptions.filter(option => availableReminderTypes.includes(option.value))
    : allReminderTypeOptions

  const hasSingleReminderType = reminderTypeOptions.length === 1
  const singleReminderType = hasSingleReminderType ? reminderTypeOptions[0].value : undefined

  // Auto-select the reminder type if there's only one option
  useEffect(() => {
    if (singleReminderType && !reminderType) {
      setReminderType(singleReminderType)
    }
  }, [singleReminderType, reminderType])

  return (
    <div className="flex flex-col pb-2 pt-4">
      <div className="flex flex-col gap-y-4">
        {!hasSingleReminderType && (
          <RadioGroup
            label={L.admin_panel.reminders.reminder_type}
            options={reminderTypeOptions}
            value={reminderType}
            onChange={setReminderType}
          />
        )}
        {reminderType === 'account_balance' && (
          <>
            <div className="text-gray-700">
              {L.admin_panel.accounts.balance_threshold_desc}
            </div>
            <div>
              <Select 
                options={ACCOUNTS_VALUES}
                description={L.admin_panel.accounts.account_desc}
                label={L.admin_panel.accounts.account}
                labelLocation="above"
                value={ACCOUNTS_VALUES.find((v) => v.value === account)}
                onChange={(value) => setAccount(value.value as Account['kind'])}
              />
            </div>
            <div>
              <Input
                value={thresholdAmount} 
                onChange={(e) => {setThresholdAmount(e.target.value)}} 
                isCurrency={true}
                currencySymbol='$'
                decimalScale={2}
                label={L.admin_panel.accounts.threshold_amt}
              />
            </div>
            <div>
              <InputWithTags 
                validate={onlyEmails}
                errorMessage={L.admin_panel.accounts.email_validation_error_msg}
                label={L.admin_panel.accounts.recipients}
                placeholder={L.admin_panel.accounts.recipients_desc}
                value={recipients}
                onChange={setRecipients}
              />
            </div>
          </>
        )}
        {reminderType === 'payment_failure' && (
          <>
            <div className="text-gray-700">
              {L.admin_panel.reminders.payment_failure_desc}
            </div>
            <div>
              <Select
                options={[
                  { label: L.admin_panel.reminders.immediate, value: 'immediate' },
                  { label: L.admin_panel.reminders.hourly, value: 'hourly' },
                  { label: L.admin_panel.reminders.daily, value: 'daily' },
                ]}
                description="Choose how often you want to be notified about new payment failures"
                label={L.admin_panel.reminders.frequency}
                labelLocation="above"
                value={[
                  { label: L.admin_panel.reminders.immediate, value: 'immediate' }, 
                  { label: L.admin_panel.reminders.hourly, value: 'hourly' },
                  { label: L.admin_panel.reminders.daily, value: 'daily' },
                ].find((v) => v.value === failureFrequency)}
                onChange={(value) => setFailureFrequency(value.value as 'immediate' | 'hourly' | 'daily')}
              />
            </div>
            <div>
              <InputWithTags 
                validate={onlyEmails}
                errorMessage={L.admin_panel.accounts.email_validation_error_msg}
                label={L.admin_panel.accounts.recipients}
                placeholder={L.admin_panel.accounts.recipients_desc}
                value={failureRecipients}
                onChange={setFailureRecipients}
              />
            </div>
          </>
        )}
        {reminderType === 'payment_status_summary' && (
          <>
            <div className="text-gray-700">
              {L.admin_panel.reminders.payment_status_summary_desc}
            </div>
            <div>
              <Select
                options={[
                  { label: L.admin_panel.reminders.daily, value: 'daily' },
                  { label: L.admin_panel.reminders.weekly, value: 'weekly' },
                ]}
                description="Choose how often you want to receive payment status summaries"
                label={L.admin_panel.reminders.frequency}
                labelLocation="above"
                value={[
                  { label: L.admin_panel.reminders.daily, value: 'daily' },
                  { label: L.admin_panel.reminders.weekly, value: 'weekly' },
                ].find((v) => v.value === summaryFrequency)}
                onChange={(value) => setSummaryFrequency(value.value as 'daily' | 'weekly')}
              />
            </div>
            <div>
              <InputWithTags 
                validate={onlyEmails}
                errorMessage={L.admin_panel.accounts.email_validation_error_msg}
                label={L.admin_panel.accounts.recipients}
                placeholder={L.admin_panel.accounts.recipients_desc}
                value={summaryRecipients}
                onChange={setSummaryRecipients}
              />
            </div>
          </>
        )}
        <div role="alert">
          {reminderCreated && (
            <Alert className="p-0 py-3 px-4" variant='success'>{L.admin_panel.reminders.success_msg}</Alert>
          )}
        </div>
      </div>
      <div className="pt-4 flex justify-end gap-x-2">
        <Button onClick={() => setRemindersModalOpen(false)} variant="secondary">{L.admin_panel.accounts.cancel}</Button>
        <Button isDisabled={createDisabled} onClick={() => createReminder()}>{L.admin_panel.accounts.create_reminder}</Button>
      </div>
    </div>
  )
}

type ReminderCardProps = {
  reminder: AccountBalanceReminder | PaymentReminder
}

const ReminderCard: React.FC<ReminderCardProps> = ({ reminder }) => {
  const L = useLocalizedStrings()

  const { editAccountBalanceReminder, editPaymentReminder, reminderCallInFlight } = useAccounts()

  const [isInEditMode, setIsInEditMode] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const isAccountBalanceReminder = (r: AccountBalanceReminder | PaymentReminder): r is AccountBalanceReminder => {
    return 'account' in r && 'thresholdInCents' in r
  }

  const isAccountBalance = isAccountBalanceReminder(reminder)

  // Account balance reminder state
  const [thresholdAmount, setThresholdAmount] = useState<string>(
    isAccountBalance ? String(reminder.thresholdInCents * 0.01) : ''
  )
  
  // Payment reminder state
  const [frequency, setFrequency] = useState<'immediate' | 'hourly' | 'daily' | 'weekly' | undefined>(
    !isAccountBalance ? reminder.frequency : undefined
  )
  
  // Shared state
  const [recipients, setRecipients] = useState<Array<string>>([...reminder.emails])

  const bullet = '\u2022'

  // Card title logic
  const cardTitle = isAccountBalance ? (
    <div className="flex items-center gap-2">
      <Icon className="text-blue-600" strokeWidth={2} name="BellIcon" size="md" role="decorative" />
      <div className="font-medium text-black">{accountKindToName(reminder.account)} {L.admin_panel.accounts.balance}</div>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <Icon className="text-blue-600" strokeWidth={2} name="BellIcon" size="md" role="decorative" />
      <div className="font-medium text-black">
        {reminder.type === 'payment_failure' ? 'Payment Failure' : 'Payment Status Summary'}
      </div>
    </div>
  )

  const cardHeader = (
    <div className="flex justify-between">
      {cardTitle}
      <div className="flex gap-2">
        <Button
          className="!p-0.5 !cursor-pointer hover:text-blue-600"
          icon={<Icon
            strokeWidth={1.5}
            className="w-1/8"
            name="PencilSquareIcon"
            role="decorative"
          />}
          variant="unstyled"
          size="sm"
          onClick={() => {
            setIsInEditMode(true)
          }}
          aria-label={L.admin_panel.accounts.edit_reminder}
        />
        <Button
          className="!p-0.5 !cursor-pointer hover:text-red-600"
          icon={<Icon
            strokeWidth={1.5}
            className="w-1/8"
            name="TrashIcon"
            role="decorative"
          />}
          variant="unstyled"
          size="sm"
          onClick={() => {
            setDeleteModalOpen(true)
          }}
          aria-label={L.admin_panel.accounts.delete_reminder}
        />
      </div>
    </div>
  )

  const cancelEdit = useCallback(() => {
    if (isAccountBalance) {
      setThresholdAmount(String(reminder.thresholdInCents * 0.01))
    } else {
      setFrequency(reminder.frequency)
    }
    setRecipients([...reminder.emails])
    setIsInEditMode(false)
  }, [reminder, isAccountBalance])

  const saveEdits = useCallback(async () => {
    if (isAccountBalance) {
      await editAccountBalanceReminder({
        reminderKey: reminder.key,
        thresholdAmount: Number(thresholdAmount) * 100,
        recipients: recipients
      })
    } else {
      await editPaymentReminder({
        reminderKey: reminder.key,
        frequency: frequency,
        recipients: recipients
      })
    }
    setIsInEditMode(false)
  }, [reminder, isAccountBalance, thresholdAmount, frequency, recipients, editAccountBalanceReminder, editPaymentReminder])

  const frequencyOptions = !isAccountBalance && reminder.type === 'payment_failure'
    ? [
      { label: L.admin_panel.reminders.immediate, value: 'immediate' },
      { label: L.admin_panel.reminders.hourly, value: 'hourly' },
      { label: L.admin_panel.reminders.daily, value: 'daily' },
    ]
    : !isAccountBalance
      ? [
        { label: L.admin_panel.reminders.daily, value: 'daily' },
        { label: L.admin_panel.reminders.weekly, value: 'weekly' },
      ]
      : []

  const editModeCardBody = (
    <div className="pt-2 pb-4 flex flex-col gap-y-4">
      {isAccountBalance ? (
        <div>
          <Input
            value={thresholdAmount} 
            onChange={(e) => {setThresholdAmount(e.target.value)}} 
            isCurrency={true}
            currencySymbol='$'
            decimalScale={2}
            label={L.admin_panel.accounts.threshold_amt}
          />
        </div>
      ) : (
        <div>
          <Select
            options={frequencyOptions}
            description="Choose how often you want to be notified"
            label="Notification Frequency"
            labelLocation="above"
            value={frequencyOptions.find((v) => v.value === frequency)}
            onChange={(value) => setFrequency(value.value as 'immediate' | 'hourly' | 'daily' | 'weekly')}
          />
        </div>
      )}
      <div>
        <InputWithTags 
          validate={onlyEmails}
          errorMessage={L.admin_panel.accounts.email_validation_error_msg}
          label={L.admin_panel.accounts.recipients}
          placeholder={L.admin_panel.accounts.recipients_desc}
          value={recipients}
          onChange={setRecipients}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={cancelEdit}>{L.admin_panel.accounts.cancel}</Button>
        <Button 
          isDisabled={reminderCallInFlight}
          onClick={async () => await saveEdits()}
        >
          {L.admin_panel.accounts.save}
        </Button>
      </div>
    </div>
  )

  const displayCardBody = (
    <div className="flex flex-col gap-2 py-2">
      {isAccountBalance ? (
        <div>
          {L.admin_panel.accounts.threshold}: <CurrencyDisplay locale="en-US" currency='USD' value={reminder.thresholdInCents * 0.01}/>
        </div>
      ) : (
        <div>
          {L.admin_panel.reminders.frequency}: <Badge variant="neutral">{frequencyOptions.find((v) => v.value === frequency)?.label || frequency}</Badge>
        </div>
      )}
      <div className="flex gap-2">
        {reminder.emails.map((email, idx) => (
          <Badge key={idx} variant="neutral">{email}</Badge>
        ))}
      </div>
    </div>
  )

  const deleteReminder = useCallback(async () => {
    if (isAccountBalance) {
      await editAccountBalanceReminder({reminderKey: reminder.key, active: false})
    } else {
      await editPaymentReminder({reminderKey: reminder.key, active: false})
    }
    setDeleteModalOpen(false)
  }, [reminder, isAccountBalance, editAccountBalanceReminder, editPaymentReminder])

  const deleteModal = (
    <Modal title={L.admin_panel.accounts.delete_reminder_title} open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
      <div>{L.admin_panel.accounts.delete_reminder_confirmation_msg}</div>
      <div className="flex pt-2 justify-end">
        <Button isDisabled={reminderCallInFlight} onClick={async () => await deleteReminder()} variant="danger">{L.admin_panel.accounts.delete}</Button>
      </div>
    </Modal>
  )
  
  return (
    <Card className="rounded-md">
      <CardHeader title={cardHeader} />
      <CardBody className="text-sm">
        {isInEditMode ? editModeCardBody : displayCardBody }
        {deleteModal}
      </CardBody>
      <CardFooter>
        <div className="text-sm text-gray-500">{L.admin_panel.accounts.created_by} {reminder.createdBy} {bullet} {L.admin_panel.accounts.last_modified} {generateDateString(reminder.lastUpdated)}</div>
      </CardFooter>
    </Card>
  )
}

const ActiveRemindersTab = () => {
  const L = useLocalizedStrings()

  const { reminderCallInFlight, accountBalanceReminders, paymentReminders } = useAccounts()

  const allReminders: Array<AccountBalanceReminder | PaymentReminder> = [
    ...accountBalanceReminders,
    ...paymentReminders
  ]

  const emptyState = (
    <div className="h-[24rem] flex items-start justify-center h-full text-center text-gray-700 min-h-[20rem] pt-4">
      <span>{L.admin_panel.reminders.active_reminders_empty_state}</span>
    </div>
  )

  const nonEmptyState = (<div tabIndex={0} className="h-[23rem] overflow-auto flex flex-col gap-2 pt-2">
    {
      allReminders.length === 0 && !reminderCallInFlight 
        ? emptyState 
        : allReminders.map((reminder, idx) => {
          return (<div key={idx}>
            <ReminderCard reminder={reminder} />
          </div>)
        })
    }
  </div>)

  return allReminders.length === 0 && !reminderCallInFlight 
    ? emptyState 
    : nonEmptyState
}

type ManagePaymentRemindersModalProps = {
  remindersModalOpen: boolean,
  setRemindersModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  availableReminderTypes?: string[]
}

export const ManagePaymentRemindersModal: React.FC<ManagePaymentRemindersModalProps> = ({
  remindersModalOpen,
  setRemindersModalOpen,
  availableReminderTypes,
}) => {
  const L = useLocalizedStrings()

  const { reminderCallInFlight, accountBalanceReminders, paymentReminders, fetchAccountBalanceReminders, fetchPaymentReminders } = useAccounts()
  
  const hasPaymentReminderTypes = availableReminderTypes 
    ? availableReminderTypes.some(type => type === 'payment_failure' || type === 'payment_status_summary')
    : true // If no filter, assume all types are available
    
  useAsyncEffect(async () => {
    if (remindersModalOpen) {
      await fetchAccountBalanceReminders()
      if (hasPaymentReminderTypes) {
        await fetchPaymentReminders()
      }
    }
  }, [remindersModalOpen, hasPaymentReminderTypes, fetchAccountBalanceReminders, fetchPaymentReminders])
    
  const totalRemindersCount = accountBalanceReminders.length + (hasPaymentReminderTypes ? paymentReminders.length : 0)
  
  const tabs = [
    { name: L.admin_panel.accounts.create_reminder, key: 'create_reminder', content: <CreateReminderTab setRemindersModalOpen={setRemindersModalOpen} availableReminderTypes={availableReminderTypes} /> },
    { 
      name: L.admin_panel.accounts.active_reminders, 
      key: 'active_reminders', 
      content: <ActiveRemindersTab />,
      count: totalRemindersCount > 0 ? { value: totalRemindersCount, level: 'info' as const } : undefined
    }
  ]
  return (
    <Modal size="lg" preventBackdropClick={reminderCallInFlight} title={L.admin_panel.accounts.manage_reminders} open={remindersModalOpen} onClose={() => setRemindersModalOpen(false)}>
      <Tabs unmountTabs tabs={tabs} />
    </Modal>
  )
}